import { api } from '../../../general/apiScenarios';
import { CHNode, CHNodeIsFolder } from '../../../general/node';
import { Interrupt } from '../../../general/scenarioFactory';
import { partition } from 'lodash';
import { CREATIVE_NAME_RULES } from '../creativeFolder/model';
import {
  CreativeBLSettings,
  dictionaryIsLibDictionary,
  specificFields,
} from './legacy_models/model';
import type { FallbackDictionary, NodeFallback } from './legacy_models/fallback';
import type { ValidationIssue } from './contracts';

export type ConvertSourceFile = {
  option: string;
  node: CHNode;
  relpath: string;
  pathSegments: string[];
  issues: ValidationIssue[];
  excluded: boolean;
  exclusionReason?: string;
};

export interface CollectConvertSourceFilesOptions {
  rootNodeId: number;
  settings: CreativeBLSettings;
  getNodes: (nodeId: number) => Promise<CHNode[]>;
}

export interface NormalizeConvertRootNodeOptions {
  rootNode: CHNode;
  settings: CreativeBLSettings;
}

export function validateConvertRootNode({
  rootNode,
  settings,
}: NormalizeConvertRootNodeOptions): string | null {
  if (
    settings.creative.node_name.length === 0 ||
    settings.creative.node_name.match(CREATIVE_NAME_RULES.FORBIDDEN)
  ) {
    return `Folder name contains not allowed characters: ${CREATIVE_NAME_RULES.HINT}`;
  }

  if (rootNode.name === settings.creative.node_name) {
    return null;
  }

  return null;
}

export async function normalizeConvertRootNode({
  rootNode,
  settings,
}: NormalizeConvertRootNodeOptions): Promise<void> {
  const validationError = validateConvertRootNode({ rootNode, settings });
  if (validationError) {
    throw Interrupt.Exception(validationError);
  }

  if (rootNode.name !== settings.creative.node_name) {
    const result = await api.fs.rename(settings.creative.node_name, rootNode.id);
    if (!result.success) {
      throw Interrupt.Exception(`Failed to rename folder to ${settings.creative.node_name}`);
    }
  }
}

export function getSubfolderDictFromTypeID(typeID: number): string {
  const langDict = specificFields.LIB_LANGUAGE_DICTIONARY;
  const platformDict = specificFields.LIB_PLATFORM_DICTIONARY;

  const subfolderNameSources: Record<number, string | undefined> = {
    1: langDict,
    2: langDict,
    3: platformDict,
  };

  return subfolderNameSources[typeID] ?? langDict;
}

export function getSubfolderFieldLabel(typeID: number): string {
  const dictName = getSubfolderDictFromTypeID(typeID);
  if (dictName === specificFields.LIB_PLATFORM_DICTIONARY) return 'platform';
  if (dictName === specificFields.LIB_LANGUAGE_DICTIONARY) return 'language';
  return dictName;
}

export function getSubfolderNameOptions(settings: CreativeBLSettings): string[] {
  const subfolderNameSource = getSubfolderDictFromTypeID(settings.type.id);
  const dict = settings.dictionaries
    .filter(dictionaryIsLibDictionary)
    .find((d) => d.name === subfolderNameSource);

  if (!dict || !dict.values) {
    return [];
  }

  return Array.isArray(dict.values) ? dict.values : [];
}

function getAllowedPathSegmentLookup(settings: CreativeBLSettings): Set<string> {
  return new Set(
    settings.dictionaries
      .filter(dictionaryIsLibDictionary)
      .flatMap((dictionary) => dictionary.values ?? [])
      .map((value) => value.toLowerCase()),
  );
}

function resolveExcludedPathInfo(pathSegments: string[], allowedPathSegments: Set<string>) {
  const disallowedSegment = pathSegments.find(
    (segment) => !allowedPathSegments.has(segment.toLowerCase()),
  );

  if (!disallowedSegment) {
    return { excluded: false as const, exclusionReason: undefined };
  }

  return {
    excluded: true as const,
    exclusionReason: `Path segment "${disallowedSegment}" is not present in lib dictionaries and starts in Excluded.`,
  };
}

export async function collectConvertSourceFiles({
  rootNodeId,
  settings,
  getNodes,
}: CollectConvertSourceFilesOptions): Promise<ConvertSourceFile[]> {
  const subfolderNameOptions = getSubfolderNameOptions(settings);
  const fieldLabel = getSubfolderFieldLabel(settings.type.id);
  const allowedPathSegments = getAllowedPathSegmentLookup(settings);

  const visitFolder = async (
    nodeId: number,
    pathSegments: string[],
  ): Promise<ConvertSourceFile[]> => {
    const nodes = await getNodes(nodeId);
    const [folderNodes, fileNodes] = partition(nodes, CHNodeIsFolder);
    const items: ConvertSourceFile[] = [];

    for (const fileNode of fileNodes) {
      const issues: ValidationIssue[] = [];
      const { excluded, exclusionReason } = resolveExcludedPathInfo(
        pathSegments,
        allowedPathSegments,
      );
      const matchedOptions = pathSegments
        .map((segment) =>
          subfolderNameOptions.find((option) => option.toLowerCase() === segment.toLowerCase()),
        )
        .filter((value): value is string => Boolean(value));
      const uniqueOptions = Array.from(new Set(matchedOptions));
      const option = uniqueOptions.at(-1) ?? '';

      if (pathSegments.length > 0 && subfolderNameOptions.length === 0) {
        issues.push({
          code: 'path-mapping-dictionary-empty',
          severity: 'error',
          message: `No ${fieldLabel} values are available for path mapping. Fill the ${fieldLabel} manually after opening the editor.`,
        });
      } else if (pathSegments.length > 0 && uniqueOptions.length === 0) {
        issues.push({
          code: 'path-mapping-not-found',
          severity: 'warning',
          message: `Path did not resolve a ${fieldLabel}. The filename or manual edit must fill it.`,
        });
      } else if (uniqueOptions.length > 1) {
        issues.push({
          code: 'path-mapping-ambiguous',
          severity: 'warning',
          message: `Path matched multiple ${fieldLabel} values (${uniqueOptions.join(', ')}). Using ${option}.`,
        });
      }

      items.push({
        option,
        node: fileNode,
        relpath: pathSegments.join('/'),
        pathSegments,
        issues,
        excluded,
        exclusionReason,
      });
    }

    for (const folderNode of folderNodes) {
      const nestedItems = await visitFolder(folderNode.id, [...pathSegments, folderNode.name]);
      items.push(...nestedItems);
    }

    return items;
  };

  return visitFolder(rootNodeId, []);
}

export function buildConvertNodeFallbacks(
  items: ConvertSourceFile[],
  creativeTypeId: number,
  fallback: FallbackDictionary,
): NodeFallback[] {
  const subfolderNameSource = getSubfolderDictFromTypeID(creativeTypeId);

  return items.map((item) => ({
    ...item,
    fallback,
    forcedMeta: {
      [subfolderNameSource]: item.option,
    },
    issues: item.issues,
    pathSegments: item.pathSegments,
    excluded: item.excluded,
    exclusionReason: item.exclusionReason,
  }));
}
